import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { settingsAPI } from '../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// POSDisplay — Müşteri Ekranı (çift-monitör POS terminallerinde 2. ekran)
// POSTerminal ile BroadcastChannel('pos-display') üzerinden senkronize çalışır.
// /pos-display rotasında tam ekran kiosk modunda açılır.
//
// Tasarım kuralları:
//  - h-screen overflow-hidden — büyük ekranda 6+ ürün eklenince total panel
//    alta yapışık kalsın, content flex-1 overflow-y-auto ile içeride scroll'da
//    kalsın (önceden min-h-screen body scroll yapıyordu, total gizleniyordu).
//  - Boş ekranda (cart=0) reklam görselleri (carousel) göster — `restaurants.settings`
//    içindeki `pos_display_ad_urls` (newline-separated URL list) veya `pos_display_ad_url`
//    (single URL) üzerinden gelir. Yoksa default karşılama ekranı.
// ─────────────────────────────────────────────────────────────────────────────

function fmt(n) {
  return '₺' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const CHANNEL_NAME = 'pos-display';
const AD_ROTATE_MS = 8000; // reklam değişim aralığı

export default function POSDisplay() {
  const { t } = useLanguage();
  const [state, setState] = useState({
    cart: [],
    subtotal: 0,
    discountAmount: 0,
    afterDiscount: 0,
    taxTotal: 0,
    paymentOpen: false,
    saleComplete: false,
    restaurantName: 'RestaFlex POS',
  });

  // Reklam URL listesi — restaurant settings'ten yüklenir
  const [adUrls, setAdUrls] = useState([]);
  const [adIndex, setAdIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    settingsAPI.get()
      .then(res => {
        if (cancelled) return;
        const data = res?.data?.data || res?.data || {};
        const settings = data.settings || data;
        // Esnek format: pos_display_ad_urls (array veya newline-string), pos_display_ad_url (single)
        let urls = [];
        const raw = settings?.pos_display_ad_urls ?? settings?.pos_display_ad_url;
        if (Array.isArray(raw)) urls = raw;
        else if (typeof raw === 'string') urls = raw.split(/\n|,/).map(s => s.trim()).filter(Boolean);
        // Restoran kapağı son fallback
        if (!urls.length && settings?.menu_cover_url) urls = [settings.menu_cover_url];
        setAdUrls(urls);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Reklam carousel (>1 görsel ise)
  useEffect(() => {
    if (adUrls.length < 2) return;
    const id = setInterval(() => setAdIndex(i => (i + 1) % adUrls.length), AD_ROTATE_MS);
    return () => clearInterval(id);
  }, [adUrls.length]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof window.BroadcastChannel !== 'function') {
      // Eski tarayıcı / iframe blocked — localStorage fallback ile devam
      console.warn('BroadcastChannel desteklenmiyor, localStorage fallback kullanılıyor.');
    }

    let channel;
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (e) => {
        if (e.data && e.data.type === 'POS_STATE') {
          setState(e.data.payload);
        }
      };
    } catch { /* BroadcastChannel undefined or blocked */ }

    const handleStorage = (e) => {
      if (e.key === CHANNEL_NAME) {
        try {
          const payload = JSON.parse(e.newValue);
          setState(payload);
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      if (channel) channel.close();
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const { cart, subtotal, discountAmount, afterDiscount, taxTotal, paymentOpen, saleComplete, restaurantName } = state;
  const grandTotal = afterDiscount;

  // Tur 276: Queue durumu — sepete giren ürünler için ilk görülme zamanı
  const seenAtRef = useRef({});
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const now = Date.now();
    const seen = seenAtRef.current;
    const ids = new Set((cart || []).map((i) => i.product_id));
    (cart || []).forEach((i) => {
      if (!seen[i.product_id]) seen[i.product_id] = now;
    });
    Object.keys(seen).forEach((k) => {
      if (!ids.has(Number(k)) && !ids.has(k)) delete seen[k];
    });
  }, [cart]);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const queueStats = useMemo(() => {
    const items = cart || [];
    const totalQty = items.reduce((s, i) => s + Number(i.qty || 0), 0);
    const preparingQty = paymentOpen ? 0 : totalQty;
    const readyQty = paymentOpen ? totalQty : 0;
    const seen = seenAtRef.current;
    const waits = items
      .map((i) => seen[i.product_id])
      .filter(Boolean)
      .map((ts) => Math.floor((Date.now() - ts) / 60000));
    const avgMin = !paymentOpen && waits.length
      ? Math.round(waits.reduce((s, w) => s + w, 0) / waits.length)
      : 0;
    return { preparingQty, readyQty, avgMin };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, paymentOpen, tick]);

  // ─── Teşekkür ekranı (satış tamamlandı) ───────────────────────────────────
  if (saleComplete) {
    return (
      <div className="h-screen overflow-hidden bg-gradient-to-br from-green-600 to-emerald-700 flex flex-col items-center justify-center text-white select-none">
        <div className="text-8xl mb-8 animate-bounce" aria-hidden="true">✅</div>
        <div className="text-5xl font-black mb-4 tracking-tight">{t('posDisplayThanks')}</div>
        <div className="text-2xl font-light opacity-80">{t('posDisplayPaymentReceived')}</div>
      </div>
    );
  }

  // ─── Ödeme bekleniyor ──────────────────────────────────────────────────────
  if (paymentOpen) {
    return (
      <div className="h-screen overflow-hidden bg-gradient-to-br from-blue-700 to-indigo-800 flex flex-col items-center justify-center text-white select-none">
        <div className="text-8xl mb-8" aria-hidden="true">💳</div>
        <div className="text-4xl font-black mb-4 tracking-tight">{t('posDisplayPaymentPending')}</div>
        <div className="text-7xl md:text-8xl font-black text-yellow-300 mb-6 tabular-nums">{fmt(grandTotal)}</div>
        <div className="text-xl font-light opacity-75">{t('posDisplayWaitForCashier')}</div>
      </div>
    );
  }

  // ─── Boş sepet — reklam carousel veya hoş geldiniz ─────────────────────────
  if (cart.length === 0) {
    if (adUrls.length > 0) {
      const currentUrl = adUrls[adIndex] || adUrls[0];
      return (
        <div className="h-screen overflow-hidden bg-black flex flex-col select-none">
          {/* Header — sade, restaurant adı ve saat */}
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-8 py-4 bg-gradient-to-b from-black/70 to-transparent text-white pointer-events-none">
            <div className="text-xl font-bold tracking-tight">{restaurantName}</div>
            <div className="text-sm text-white/70 font-mono">
              {new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          {/* Reklam görseli — full screen, object-cover */}
          <img
            src={currentUrl}
            alt={t('posDisplayAdAlt') || 'Promosyon'}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Görsel yüklenmezse karşılama ekranına düş
              e.currentTarget.style.display = 'none';
            }}
          />
          {/* Alt etiket — sade */}
          <div className="absolute bottom-0 left-0 right-0 px-8 py-4 bg-gradient-to-t from-black/70 to-transparent text-white pointer-events-none">
            <div className="text-lg font-light opacity-90">{t('posDisplayTellCashier')}</div>
          </div>
          {/* Carousel indicator */}
          {adUrls.length > 1 && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-2" aria-hidden="true">
              {adUrls.map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all ${i === adIndex ? 'bg-white w-6' : 'bg-white/40'}`}
                />
              ))}
            </div>
          )}
        </div>
      );
    }
    // Reklam URL'i yoksa: sade karşılama
    return (
      <div className="h-screen overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800 flex flex-col items-center justify-center text-white select-none">
        <div className="text-7xl mb-6" aria-hidden="true">🛒</div>
        <div className="text-4xl font-black mb-3 tracking-tight">{restaurantName}</div>
        <div className="text-xl font-light text-gray-400">{t('posDisplayTellCashier')}</div>
        <div className="mt-12 text-sm text-gray-600 font-mono">{t('posDisplayCustomerScreen')}</div>
        <div className="mt-8 text-xs text-gray-700 max-w-md text-center px-6">
          {t('posDisplayAdHint') || 'Yöneticiniz Settings → Fiş Ayarları sayfasından promosyon görsellerini ekleyebilir.'}
        </div>
      </div>
    );
  }

  // ─── Sepet gösterimi ───────────────────────────────────────────────────────
  // h-screen + flex column + overflow-hidden:
  //   - header sabit
  //   - cart `flex-1 min-h-0 overflow-y-auto` (içeride scroll, viewport taşmaz)
  //   - total panel `flex-shrink-0` (her zaman ekrana yapışık)
  return (
    <div className="h-screen overflow-hidden bg-gray-950 text-white flex flex-col select-none">

      {/* Header */}
      <header className="bg-gray-900 border-b border-gray-800 px-6 lg:px-8 py-4 flex items-center justify-between flex-shrink-0">
        <div className="text-xl lg:text-2xl font-bold text-gray-200 tracking-tight">{restaurantName}</div>
        <div className="text-sm text-gray-500 font-mono tabular-nums">
          {new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
        </div>
      </header>

      {/* Sepet ürünleri (scrollable) */}
      <main className="flex-1 min-h-0 overflow-y-auto px-6 lg:px-8 py-4">
        <div className="max-w-3xl mx-auto">
          {/* Queue durumu — sade, satır içinde 3 stat */}
          {(() => {
            const { preparingQty, readyQty, avgMin } = queueStats;
            return (
              <div
                className="grid grid-cols-3 gap-2 mb-3"
                role="group"
                aria-label={t('posDisplayQueueOverview') || 'Sipariş Durumu'}
              >
                <div className="rounded-xl bg-yellow-500/10 border border-yellow-500/30 px-3 py-2 text-center">
                  <div className="text-2xl lg:text-3xl font-black text-yellow-300 leading-tight tabular-nums">{preparingQty}</div>
                  <div className="text-[11px] lg:text-xs font-semibold text-yellow-200/80 mt-0.5">{t('posDisplayQueuePreparing') || 'Hazırlanan'}</div>
                </div>
                <div className="rounded-xl bg-green-500/10 border border-green-500/30 px-3 py-2 text-center">
                  <div className="text-2xl lg:text-3xl font-black text-green-300 leading-tight tabular-nums">{readyQty}</div>
                  <div className="text-[11px] lg:text-xs font-semibold text-green-200/80 mt-0.5">{t('posDisplayQueueReady') || 'Hazır'}</div>
                </div>
                <div className="rounded-xl bg-blue-500/10 border border-blue-500/30 px-3 py-2 text-center">
                  <div className="text-2xl lg:text-3xl font-black text-blue-300 leading-tight tabular-nums">
                    {avgMin}<span className="text-base lg:text-lg font-bold ml-0.5">{t('minShort') || 'dk'}</span>
                  </div>
                  <div className="text-[11px] lg:text-xs font-semibold text-blue-200/80 mt-0.5">{t('posDisplayQueueAvgWait') || 'Ort. Bekleme'}</div>
                </div>
              </div>
            );
          })()}

          {/* Cart liste — sade, daha kompakt satırlar */}
          <ul className="space-y-2">
            {cart.map((item) => (
              <li
                key={item.product_id}
                className="flex items-center gap-3 lg:gap-4 bg-gray-900 rounded-xl px-4 lg:px-5 py-3 border border-gray-800"
              >
                <span className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center text-base font-bold flex-shrink-0 tabular-nums">
                  {item.qty}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-base lg:text-lg font-semibold text-white truncate">{item.name}</div>
                  <div className="text-xs lg:text-sm text-gray-400">{fmt(item.unit_price)}</div>
                </div>
                <div className="text-lg lg:text-xl font-bold text-white flex-shrink-0 tabular-nums">
                  {fmt(item.unit_price * item.qty)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </main>

      {/* Toplam paneli — her zaman ekran altında sabit */}
      <footer className="bg-gray-900 border-t border-gray-800 px-6 lg:px-8 py-5 flex-shrink-0">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap gap-x-6 gap-y-1 mb-3 text-sm lg:text-base">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">{t('subtotal')}:</span>
              <span className="text-gray-300 tabular-nums">{fmt(subtotal)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-green-500">{t('posDisplayDiscount')}:</span>
                <span className="text-green-400 tabular-nums">-{fmt(discountAmount)}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-gray-500">{t('posDisplayVatIncluded')}:</span>
              <span className="text-gray-400 tabular-nums">{fmt(taxTotal)}</span>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-3 flex items-center justify-between">
            <span className="text-xl lg:text-2xl font-bold text-gray-200">{t('totalLabel')}</span>
            <span className="text-3xl lg:text-5xl font-black text-yellow-400 tabular-nums">{fmt(grandTotal)}</span>
          </div>
        </div>
      </footer>

    </div>
  );
}
